import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryService } from "../services/query-service";
import { QueryKeys } from "../config/query-keys";
import { DeleteConversationResponse } from "../models/api-types";
import useAuthStore from "@/store/use-auth-store";
import { cancelRedirect } from "@/features/chat/redirect-navigation";
import { useStreamingStore } from "@/features/chat/store/streaming-store";

export function useDeleteConversation() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const mutation = useMutation({
    mutationFn: async (conversationId: string) => {
      // Yönlendirmeyi istekten ÖNCE iptal et: silme sürerken bekleyen
      // yönlendirme tetiklenirse kullanıcı, sildiği sohbetin turunun istediği
      // ekrana taşınır. Oturumun kendisine burada dokunulmuyor — silme
      // başarısız olursa sohbet çalışır durumda kalmalı.
      cancelRedirect(conversationId);
      const response = await queryService.deleteConversation(conversationId);
      // ApiClient 4xx cevaplarını retry etmeden resolve edebiliyor. React
      // Query'nin `onSuccess` kolu yalnız promise çözülmesine değil, backend'in
      // açık başarı sonucuna dayanmalı; aksi halde silinmeyen sohbetin stream
      // oturumu ve cache'i temizlenir.
      if (response.success !== true) {
        // Mesaj yoksa boş Error, çağıran ekranların çevrilmiş genel silme
        // hatasına düşmesini sağlar; iç hata kodu kullanıcıya gösterilmez.
        throw new Error(response.message || "");
      }
      return response;
    },
    onSuccess: (_data: DeleteConversationResponse, conversationId: string) => {
      // Silme başarılı. İstek uçarken yeniden kurulmuş olabilecek yönlendirmeyi
      // tekrar iptal et ve oturumu sıfırla — resetSession sürmekte olan stream'i
      // de abort ediyor. Silinen bir sohbetin taslağı ekranda kalmamalı,
      // kuyruğu boşalmamalı.
      cancelRedirect(conversationId);
      useStreamingStore.getState().resetSession(conversationId);

      // 🔄 Conversations list'i invalidate et ki silinen conversation kaybolsun
      queryClient.invalidateQueries({
        queryKey: [...QueryKeys.CONVERSATIONS.list, user?._id],
      });

      // Aktif observer'larda gereksiz refetch tetiklenmemesi için önce in-flight istekleri iptal et
      queryClient.cancelQueries({
        queryKey: QueryKeys.CONVERSATIONS.messages(conversationId),
      });

      // 🗑️ Silinen conversation'ın sadece inactive message cache'ini temizle
      // (active query remove edilirse mounted component tekrar fetch atabiliyor)
      queryClient.removeQueries({
        queryKey: QueryKeys.CONVERSATIONS.messages(conversationId),
        type: "inactive",
      });
    },
    onError: (error, conversationId) => {
      console.error("❌ Delete conversation error:", {
        conversationId,
        error,
      });
    },
  });

  return {
    deleteConversation: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error,
  };
}
